import { Injectable, inject } from '@angular/core';
import { EnvironmentService } from '@lib/core/environment';
import { FormDataFactory } from '@lib/core/factory';
import { BaseUser } from '@lib/core/model';
import { Uuid } from '@lib/core/type';
import { AuthHttpClient } from '@proget-shared/auth-http-client';
import { ResponseErrorFactory } from '@proget-shared/helper/response-error';
import { catchError, map, Observable } from 'rxjs';

import { Certificate } from './model/certificate';
import { ExternalCertificate } from './model/external-certificate';

@Injectable({
  providedIn: 'root',
})
export class UserCertificatesRepository {
  private readonly httpClient = inject(AuthHttpClient);
  private readonly environment = inject(EnvironmentService);

  public getUserSMIMECertificate(userId: Uuid): Observable<ExternalCertificate> {
    return this.httpClient
      .get<any>(`${this.environment.apiUrl}/settings/certificate/issued`, {
        params: { subjectId: userId, subjectType: 'user', generated: 'false' },
      })
      .pipe(
        map(
          (data) => (Array.isArray(data.items) && data.items.length ? data.items.map((item) => new ExternalCertificate(
            item.id,
            item.purpose,
            this.mapCertificate(item.encryptionCertificate),
            this.mapCertificate(item.signingCertificate),
            item.user ? new BaseUser(
              null,
              item.user.id,
              item.user.firstName,
              item.user.lastName,
              item.user.email
            ) : null,
            item.modifiedBy ? new BaseUser(
              null,
              item.modifiedBy.id,
              item.modifiedBy.firstName,
              item.modifiedBy.lastName,
              item.modifiedBy.email
            ) : null
          ))[0] : null)
        )
      );
  }

  public createUserSMIMECertificate(
    userId: Uuid,
    encryption: { file: File; password: string },
    signing: { file: File; password: string }
  ): Observable<void> {
    return this.httpClient.post<void>(
      `${this.environment.apiUrl}/settings/certificate/issued/smime`,
      FormDataFactory.create({
        userId,
        encryption: this.serializeCertificateData(encryption),
        signing: this.serializeCertificateData(signing),
      })
    ).pipe(
      catchError((error) => {
        throw ResponseErrorFactory.create(error);
      })
    );
  }

  public updateUserSMIMECertificate(
    certificateId: Uuid,
    encryption: { selected: boolean; uploaded: boolean; file: File; password: string },
    signing: { selected: boolean; uploaded: boolean; file: File; password: string }
  ): Observable<void> {
    return this.httpClient.post<void>(
      `${this.environment.apiUrl}/settings/certificate/issued/smime/${certificateId}`,
      FormDataFactory.create({
        encryption: encryption.uploaded && !encryption.selected ? '' : this.serializeCertificateData(encryption),
        signing: signing.uploaded && !signing.selected ? '' : this.serializeCertificateData(signing),
      })
    ).pipe(
      catchError((error) => {
        throw ResponseErrorFactory.create(error);
      })
    );
  }

  public deleteUserCertificate(id: Uuid): Observable<void> {
    return this.httpClient.delete<any>(`${this.environment.apiUrl}/settings/certificate/issued/${id}`)
      .pipe(
        catchError((error) => {
          throw ResponseErrorFactory.create(error);
        })
      );
  }

  private mapCertificate(data: any | null): Certificate | null {
    return data
      ? new Certificate(
        data.selfSigned,
        data.subject,
        Object.keys(data.extra || {}).length ? data.extra : null,
        data.issuer,
        data.validity,
        data.valid
      )
      : null;
  }

  private serializeCertificateData(data: { file: File; password: string }): any {
    if (!data.file && !data.password) {
      return {};
    }

    const serializedData: any = {};

    if (data.file) {
      serializedData.file = data.file;
    }

    if (data.password) {
      serializedData.password = data.password;
    }

    return serializedData;
  }
}
